import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, PuffLog, Stats } from '@puff/shared';

interface AppState {
  // Auth state
  isInitialized: boolean;
  isAuthenticated: boolean;
  liffError: string | null;

  // User data
  user: User | null;
  isNewUser: boolean;

  // Logs
  todayLogs: PuffLog[];
  todayCount: number;
  cravingsResisted: number;
  cravingsDate: string; // YYYY-MM-DD to reset daily

  // Stats
  stats: Stats | null;

  // UI state
  isLoading: boolean;
  activeTab: 'home' | 'stats' | 'community' | 'profile' | 'share' | 'money' | 'explore';
  openSideMenu: boolean;
  motivation: string | null;

  // Actions
  setInitialized: (initialized: boolean) => void;
  setAuthenticated: (authenticated: boolean) => void;
  setLiffError: (error: string | null) => void;
  setUser: (user: User | null, isNew?: boolean) => void;
  setOnboardingCompleted: (completed: boolean) => void;
  updateUserSettings: (settings: Partial<User>) => void;

  setTodayLogs: (logs: PuffLog[]) => void;
  addPuffLog: (log: PuffLog) => void;
  removePuffLog: (logId: string) => void;

  setStats: (stats: Stats | null) => void;
  updateTodayCount: (count: number) => void;
  incrementCravingsResisted: () => void;

  setLoading: (loading: boolean) => void;
  setActiveTab: (tab: 'home' | 'stats' | 'community' | 'profile' | 'money' | 'explore') => void;
  setMotivation: (motivation: string | null) => void;

  reset: () => void;
}

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  liffError: null,
  user: null,
  isNewUser: false,
  todayLogs: [],
  todayCount: 0,
  cravingsResisted: 0,
  cravingsDate: new Date().toISOString().split('T')[0],
  stats: null,
  isLoading: false,
  activeTab: 'home' as const,
  openSideMenu: false,
  motivation: null,
};

export const useStore = create<AppState>()(
  persist(
    (set, _get) => ({
      ...initialState,

      setInitialized: (initialized) => set({ isInitialized: initialized }),
      setAuthenticated: (authenticated) => set({ isAuthenticated: authenticated }),
      setLiffError: (error) => set({ liffError: error }),

      setUser: (user, isNew = false) => set({
        user,
        isNewUser: isNew,
        isAuthenticated: !!user,
      }),

      setOnboardingCompleted: (completed) => set((state) => ({
        user: state.user ? { ...state.user, onboardingCompleted: completed } : null,
      })),

      updateUserSettings: (settings) => set((state) => ({
        user: state.user ? { ...state.user, ...settings } : null,
      })),

      setTodayLogs: (logs) => set({
        todayLogs: logs,
        todayCount: logs.reduce((sum, log) => sum + log.count, 0),
      }),

      addPuffLog: (log) => set((state) => {
        const newLogs = [log, ...state.todayLogs];
        return {
          todayLogs: newLogs,
          todayCount: state.todayCount + log.count,
        };
      }),

      removePuffLog: (logId) => set((state) => {
        const log = state.todayLogs.find(l => l.id === logId);
        const newLogs = state.todayLogs.filter(l => l.id !== logId);
        return {
          todayLogs: newLogs,
          todayCount: state.todayCount - (log?.count || 0),
        };
      }),

      setStats: (stats) => set({ stats }),

      updateTodayCount: (count) => set({ todayCount: count }),

      incrementCravingsResisted: () => set((state) => {
        const today = new Date().toISOString().split('T')[0];
        // Reset if it's a new day
        if (state.cravingsDate !== today) {
          return { cravingsResisted: 1, cravingsDate: today };
        }
        return { cravingsResisted: state.cravingsResisted + 1 };
      }),

      setLoading: (loading) => set({ isLoading: loading }),

      setActiveTab: (tab) => {
        set({ activeTab: tab });
        // Push URL to browser history so back button works
        const url = new URL(window.location.href);
        if (tab === 'home') {
          url.searchParams.delete('tab');
        } else {
          url.searchParams.set('tab', tab);
        }
        window.history.pushState({ tab }, '', url.toString());
      },

      setMotivation: (motivation) => set({ motivation }),

      reset: () => set(initialState),
    }),
    {
      name: 'puff-tracker-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        todayLogs: state.todayLogs,
        todayCount: state.todayCount,
        cravingsResisted: state.cravingsResisted,
        cravingsDate: state.cravingsDate,
        motivation: state.motivation,
        // activeTab removed from persistence to prevent stuck state
      }),
    }
  )
);

// Selectors
export const selectDailyProgress = (state: AppState) => {
  if (!state.user) return { percentage: 0, current: 0, limit: 0 };
  const limit = state.user.dailyPuffLimit;
  const current = state.todayCount;
  const percentage = Math.min((current / limit) * 100, 100);
  return { percentage, current, limit };
};

export const selectTimeSinceLastPuff = (state: AppState): string | null => {
  if (state.todayLogs.length === 0) return null;
  const lastLog = state.todayLogs[0];
  const diff = Date.now() - new Date(lastLog.loggedAt).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} min${minutes > 1 ? 's' : ''}`;
  }
  return `${minutes} min${minutes > 1 ? 's' : ''}`;
};
